import { fetchPath } from "../util/file/fetch-path";
import { isVideo } from "../util/file/is-video";
import { createImgproxyUrl, ImgproxyOptions } from "./create-imgproxy-url";
import { IMGPROXY_URL } from "./imgproxy-config";

/**
 * Widths offered to the browser, in CSS pixels. The small end matters: without
 * it a 28px avatar picks a 320-wide file. Only the width the browser picks is
 * ever rendered.
 */
export const IMGPROXY_SRCSET_WIDTHS = [
  96, 160, 256, 384, 480, 640, 828, 1080, 1200, 1920,
];

/** What `src` falls back to where `srcset` is ignored. */
const FALLBACK_WIDTH = 1080;

/** Re-encoding at the source format can exceed the original: a 2.4MB PNG came
 * back as 2.7MB at the widest rung, and 241KB as WebP. */
const EXTENSION = ".webp";

export interface ImgproxySrcSet {
  src: string;
  srcSet: string;
}

/**
 * A GIF or WebP may be animated and would lose its frames (imgproxy keeps the
 * first), an SVG scales on its own, a video isn't an image, and resizing an
 * imgproxy URL asks imgproxy to fetch itself, which fails.
 */
const isResizable = (origin: string) =>
  /^https?:\/\//.test(origin) &&
  !origin.startsWith(IMGPROXY_URL) &&
  !isVideo(origin) &&
  !/\.(gif|webp|svg)$/i.test(fetchPath(origin));

/**
 * A `src` / `srcset` pair for one source image — the responsive counterpart to
 * {@link createImgproxyUrl}. `undefined` for a source that can't be resized
 * (see {@link isResizable}) or that imgproxy can't reach, such as a bundled
 * asset or a data URI; the caller then serves it as it is.
 */
export function createImgproxySrcSet(
  origin: string | undefined,
  options: ImgproxyOptions = {}
): ImgproxySrcSet | undefined {
  if (!origin || !isResizable(origin)) return undefined;

  const at = (width: number) =>
    createImgproxyUrl(origin, {
      ...options,
      // The legacy screen-based mode would override the width asked for here.
      responsive: undefined,
      width,
      extension: EXTENSION,
    });

  return {
    src: at(FALLBACK_WIDTH),
    srcSet: IMGPROXY_SRCSET_WIDTHS.map(
      (width) => `${at(width)} ${width}w`
    ).join(", "),
  };
}
